import {Component, Input, OnInit} from '@angular/core';
import {Router} from '@angular/router';

import {TranslatePipe} from '@ngx-translate/core';
import {firstValueFrom, tap} from 'rxjs';
import {LoggingService} from "../../../core/service/logging.service";
import {DiscountService} from "../../../apps/subscriptions/shared/service/discount.service";
import {AppliedCouponModalComponent} from "../applied-coupon-modal/applied-coupon-modal.component";
import {HttpErrorResponse} from "@angular/common/http";

import {LanguageService} from "../../../core/i18n/language.service";
import {ProductCacheService} from "../../service/product-cache.service";
import {CurrencyCode, DiscountState, PlanOffer, PricingInfo, SubscriptionLevel, UPGRADE_PLAN_BUDGETS} from "./upgrade-modal.model";
import {formatDiscountValue} from "../../../apps/subscriptions/shared/model/discount/discount-pricing";
import {ActiveModal, ModalService} from "../../modal";
import {IconComponent} from "../icon/icon.component";
import {LimitFailure} from '../../limits/limit-failure.model';

/**
 * The copy of every offer is price-independent in all locales, so the offer keeps its context
 * (limit, images, audio, ...) whether or not a coupon or a price is known. Only the CTA and the
 * plan cards follow the discount state. `zero.100.*` keeps the shared "choose a plan" CTA and
 * the plan-card lines.
 */
const CHOOSE_PLAN_BUTTON_KEY = 'upgrade.modal.zero.100.button';

@Component({
    selector: 'app-upgrade-modal',
    templateUrl: './upgrade-modal.component.html',
    styleUrl: './upgrade-modal.component.scss',
    host: {class: 'modal-fit -m-6'},
    standalone: true,
    imports: [TranslatePipe, IconComponent]
})
export class UpgradeModalComponent implements OnInit {
    @Input() subscriptionLevel: SubscriptionLevel = 'zero';
    @Input() featureLimitVariant?: number;
    @Input() discountState?: DiscountState;
    @Input() pricingInfo?: PricingInfo | null;
    @Input() advancedPricingInfo?: PricingInfo | null;
    @Input() source = 'feature';
    @Input() limit?: LimitFailure;
    @Input() chatId?: string;
    @Input() interruptionId?: string;

    protected variantNumber: number = 1;
    protected originalVariantNumber: number = 1;
    protected processing: boolean = false;

    private readonly COUPON_CODE = '1STTIME50OFF';

    constructor(
        public activeModal: ActiveModal,
        private router: Router,
        private readonly logger: LoggingService,
        private readonly languageService: LanguageService,
        private readonly discountService: DiscountService,
        private readonly modalService: ModalService,
        private readonly productCacheService: ProductCacheService
    ) {
    }

    ngOnInit(): void {
        if (this.subscriptionLevel === 'zero') {
            this.originalVariantNumber = this.featureLimitVariant !== undefined && [6, 7, 8, 9, 12].includes(this.featureLimitVariant)
                ? this.featureLimitVariant : 1;
            this.variantNumber = this.originalVariantNumber;
        }
        this.logVariantShown(this.subscriptionLevel === 'zero' ? this.variantNumber : null);
    }

    get discountAvailable(): boolean {
        return this.discountState?.available ?? false;
    }

    get discountPending(): boolean {
        return this.discountState?.pending ?? false;
    }

    /** Plan cards and the plan comparison belong to the FREE offer only; paid plans compare on the pricing page. */
    get isFreeOffer(): boolean {
        return this.subscriptionLevel === 'zero';
    }

    get planOffers(): PlanOffer[] {
        const offers: PlanOffer[] = [];
        if (!this.isFreeOffer) {
            return offers;
        }
        if (this.pricingInfo) {
            offers.push({plan: 'basic', pricing: this.pricingInfo,
                freeLimitMultiple: UPGRADE_PLAN_BUDGETS.basic / UPGRADE_PLAN_BUDGETS.zero});
        }
        if (this.advancedPricingInfo) {
            offers.push({plan: 'advanced', pricing: this.advancedPricingInfo,
                freeLimitMultiple: UPGRADE_PLAN_BUDGETS.advanced / UPGRADE_PLAN_BUDGETS.zero});
        }
        return offers;
    }

    get advancedLimitMultiple(): string {
        return new Intl.NumberFormat(this.languageService.getCurrentLanguageSnapshot().code)
            .format(UPGRADE_PLAN_BUDGETS.advanced / UPGRADE_PLAN_BUDGETS.basic);
    }

    /**
     * An already applied coupon reads as "your discount is active", an offered one as a welcome
     * discount — otherwise the second modal after applying it looked identical to the first.
     */
    get discountBadgeKey(): string {
        return this.discountPending ? 'upgrade.modal.pricing.discount-active' : 'upgrade.modal.pricing.discount-badge';
    }

    get priceDifference(): string | null {
        const basic = this.pricingInfo;
        const advanced = this.advancedPricingInfo;
        if (!this.isFreeOffer || !basic || !advanced || basic.currency !== advanced.currency
            || basic.hasDiscount !== advanced.hasDiscount) return null;
        const difference = (advanced.hasDiscount ? advanced.discountedPrice : advanced.originalPrice)
            - (basic.hasDiscount ? basic.discountedPrice : basic.originalPrice);
        return difference > 0 ? this.productCacheService.formatPrice(difference, basic.currency) : null;
    }

    getTranslationPrefix(): string {
        switch (this.subscriptionLevel) {
            case 'zero':
                return `upgrade.modal.zero.${this.variantNumber}`;
            case 'basic':
                return 'upgrade.modal.basic';
            case 'advanced':
                return 'upgrade.modal.advanced';
            default:
                return 'upgrade.modal.zero.1';
        }
    }

    getButtonTranslationKey(): string {
        // The offer keeps its context; only the CTA follows the discount: "claim" only when a coupon
        // is still to be claimed and its price is displayed, "choose a plan" once it is applied.
        if (this.isFreeOffer
            && (this.discountPending || !this.planOffers.some(offer => offer.pricing.hasDiscount))) {
            return CHOOSE_PLAN_BUTTON_KEY;
        }
        return this.getTranslationPrefix() + '.button';
    }

    async navigateToSubscription(): Promise<void> {
        this.logButtonClick(this.subscriptionLevel === 'zero' ? this.variantNumber : null);

        // If discount is already pending, just navigate
        if (this.discountPending) {
            this.activeModal.dismiss();
            this.router.navigate(['/page/subscription']);
            return;
        }

        // If discount is available, apply it first
        if (this.discountAvailable && this.discountState?.discount) {
            this.processing = true;
            try {
                await this.applyDiscount();
                this.activeModal.dismiss();
                this.openAppliedCouponModal(true);
            } catch (error) {
                this.logger.error('Failed to apply discount', error as HttpErrorResponse);
                this.activeModal.dismiss();
                this.openAppliedCouponModal(false);
            } finally {
                this.processing = false;
            }
        } else {
            this.activeModal.dismiss();
            this.router.navigate(['/page/subscription']);
        }
    }

    private async applyDiscount(): Promise<void> {
        await firstValueFrom(this.discountService.setPendingDiscount({
            discountCode: this.discountState!.discount!.id!,
            type: 'COUPON',
        }).pipe(tap(() => this.productCacheService.clearCache())));
    }

    private openAppliedCouponModal(success: boolean): void {
        const modalRef = this.modalService.open(AppliedCouponModalComponent, {
            centered: true,
            size: 'md',
            backdrop: 'static'
        });
        modalRef.componentInstance.success = success;
        modalRef.componentInstance.discountValue = this.getDiscountValue();
    }

    protected getDiscountValue(): string {
        const discount = this.discountState?.discount || this.discountState?.pendingDiscount;
        if (!discount) return '';
        const language = this.languageService.getCurrentLanguageSnapshot();
        const currency = this.pricingInfo?.currency ?? this.advancedPricingInfo?.currency
            ?? (language.preferredCurrency as CurrencyCode) ?? 'EUR';
        return formatDiscountValue(discount, language.code,
            amount => this.productCacheService.formatPrice(amount, currency));
    }

    close(): void {
        this.activeModal.dismiss();
    }

    private logVariantShown(variant: number | null): void {
        this.logger.info("Upgrade modal shown", {
            subscriptionLevel: this.subscriptionLevel,
            source: this.source,
            reason: this.limit?.reason,
            metric: this.limit?.metric,
            planAtFailure: this.limit?.planAtFailure,
            chatId: this.chatId,
            interruptionId: this.interruptionId,
            variant: variant,
            originalVariant: this.originalVariantNumber,
            isFeatureLimit: this.featureLimitVariant !== undefined,
            featureLimitVariant: this.featureLimitVariant,
            discountAvailable: this.discountAvailable,
            discountPending: this.discountPending,
            discountCode: (this.discountAvailable || this.discountPending) ? this.COUPON_CODE : null
        }, {language: this.languageService.getCurrentLanguageSnapshot().code});
    }

    private logButtonClick(variant: number | null): void {
        this.logger.info("Upgrade modal clicked", {
            subscriptionLevel: this.subscriptionLevel,
            source: this.source,
            reason: this.limit?.reason,
            metric: this.limit?.metric,
            planAtFailure: this.limit?.planAtFailure,
            chatId: this.chatId,
            interruptionId: this.interruptionId,
            variant: variant,
            originalVariant: this.originalVariantNumber,
            isFeatureLimit: this.featureLimitVariant !== undefined,
            featureLimitVariant: this.featureLimitVariant,
            discountAvailable: this.discountAvailable,
            discountPending: this.discountPending,
            discountCode: (this.discountAvailable || this.discountPending) ? this.COUPON_CODE : null
        }, {language: this.languageService.getCurrentLanguageSnapshot().code});
    }
}
